import { Flex, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { I18nContext } from '@lemonade-hq/nuiza';
import parse from 'html-react-parser';
import type React from 'react';
import { useContext, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import AppDownloadLinks from '../AppDownloadLinks';
import { adjustContentVisibility, Section, SectionParagraph, SectionTitle } from '../Content';
import type { DefaultSectionProps } from '../DefaultSectionProps';
import { Stars, Tweets } from './components';
import type { Tweet } from './components/TweetCard';
import type { TweetsLocale } from './defaults/tweets/getTweets';
import { getTweets } from './defaults/tweets/getTweets';

const Title = styled(SectionTitle)`
  white-space: normal;
  text-wrap: balance;
  ${MEDIA_QUERIES.desktop`
    white-space: pre;
  `}
  width: 100vw;
  padding: 0 20px;
`;

const Subtitle = styled(SectionParagraph)`
  margin-top: 20px;
  white-space: normal;
  text-wrap: balance;
  ${MEDIA_QUERIES.tabletAndDesktop`
    white-space: pre;
    width: 100%;
  `}
  width: 100vw;
  padding: 0 20px;
`;

const TweetsBackBand = styled.div<{
  readonly darkBackground: boolean;
  readonly tweetsBackBandColor?: string;
}>`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 130px;
  background: ${({ darkBackground, tweetsBackBandColor }) =>
    tweetsBackBandColor != null && tweetsBackBandColor.length > 0
      ? tweetsBackBandColor
      : darkBackground
        ? themedColor('white')
        : themedColor('cloud')};
  z-index: 0;

  ${MEDIA_QUERIES.mobile`
    height: 100px;
  `}
`;

const StyledSection = styled(Section).attrs({
  pb: [30, 50],
})`
  ${adjustContentVisibility};
`;

export interface CustomersReviewsProps extends DefaultSectionProps {
  readonly title: string;
  readonly subtitle?: string;
  readonly thirdTitle?: string;
  readonly showFullStars?: boolean;
  readonly shortCard?: boolean;
  readonly tweets?: Tweet[];
  readonly tweetsBackBandColor?: string;
  readonly showStars?: boolean;
  readonly animateOnce?: boolean;
  readonly showTweets?: boolean;
  readonly showAppDownloadLinks?: boolean;
}

export const CustomersReviews: React.FC<CustomersReviewsProps> = ({
  title,
  subtitle,
  thirdTitle,
  showFullStars = false,
  shortCard = true,
  tweets,
  tweetsBackBandColor,
  showStars = true,
  showTweets = true,
  animateOnce = true,
  showAppDownloadLinks = false,
  ...sectionProps
}): JSX.Element => {
  const { locale } = useContext(I18nContext);
  const { ref, inView } = useInView({
    triggerOnce: animateOnce,
  });

  const tweetsList = useMemo((): Tweet[] | undefined => {
    return tweets ?? getTweets(locale as unknown as TweetsLocale);
  }, [tweets, locale]);

  return (
    <StyledSection overflowX="hidden" position="relative" {...sectionProps} py={[40, 70]}>
      {showTweets && (
        <TweetsBackBand
          darkBackground={sectionProps.darkBackground ?? false}
          tweetsBackBandColor={tweetsBackBandColor}
        />
      )}
      <Flex alignItems="center" flexDirection="column" position="relative" width="100%">
        <Title tabIndex={0}>{parse(title)}</Title>

        {subtitle != null && <Subtitle tabIndex={0}>{parse(subtitle)}</Subtitle>}

        {showStars && <Stars ref={ref} scrolledTo={inView} showFullStars={showFullStars} />}

        {thirdTitle != null && <SectionParagraph>{parse(thirdTitle)}</SectionParagraph>}

        {showTweets && <Tweets shortCard={shortCard} tweets={tweetsList} />}

        {showAppDownloadLinks && <AppDownloadLinks />}
      </Flex>
    </StyledSection>
  );
};
